import { Link } from "react-router-dom";
import { PenLine, Users, ScrollText, Sparkles, CalendarRange, BookOpenText, Languages, GraduationCap, Rss, Gift, Volume2 } from "lucide-react";

const STEPS = [
  { n: "01", Icon: PenLine, title: "Enter a topic", body: "A word, a situation, or a verse — “healing”, “my marriage”, “Psalm 91”." },
  { n: "02", Icon: Users, title: "Say who it's for", body: "Yourself, a person by name, or a group. The voice shifts to I, they, or we." },
  { n: "03", Icon: ScrollText, title: "Receive your decrees", body: "10–15 present-tense declarations, each woven around its scripture reference, with a Hebrew & Greek word study." },
];

export function HowItWorks() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="how-it-works-section">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">How it works</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight mt-3">From a single word to an established decree.</h2>
          <p className="font-body text-base text-[#4A4744] mt-4 leading-relaxed">Every decree is first-person, present-tense and scripture-sourced — never a vague affirmation.</p>
        </div>
        <ol className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <li key={s.n} data-testid={`step-${s.n.slice(1)}-card`} className="border-t-2 border-[#D9D4CC] pt-6 hover:border-[#9B7E4E] transition-colors duration-300">
              <div className="flex items-center justify-between">
                <s.Icon className="w-6 h-6 text-[#9B7E4E]" strokeWidth={1.5} />
                <span className="font-heading text-2xl text-[#D9D4CC]">{s.n}</span>
              </div>
              <h3 className="font-heading text-xl text-[#2C2A29] mt-5">{s.title}</h3>
              <p className="font-body text-sm text-[#4A4744] mt-2 leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const FEATURES = [
  { id: "generator", Icon: Sparkles, title: "Decree Generator", body: "Personal, intercessory or corporate decrees with anti-repetition across your history, a focus-word option, and private-or-public posting.", span: "lg:col-span-7 lg:row-span-2", to: "/generate" },
  { id: "plans", Icon: CalendarRange, title: "Decree Plans", body: "3 to 40-day guided journeys with daily decrees, streaks and progress.", span: "lg:col-span-5", to: "/plans" },
  { id: "bible-reader", Icon: Languages, title: "Bible in 9 translations", body: "KJV, WEB, ASV, BSB, YLT, Darby, BBE, Webster, Douay-Rheims — with four-color highlights and “Decree this verse”.", span: "lg:col-span-5", to: "/bible" },
  { id: "lexicon", Icon: BookOpenText, title: "Lexicon & Strong's", body: "14,000+ Hebrew and Greek entries with AI word studies and every verse occurrence.", span: "lg:col-span-4", to: "/lexicon" },
  { id: "teachings", Icon: GraduationCap, title: "Teachings", body: "Kingdom 101 and ICU Level 2 courses with progress tracking and read-aloud.", span: "lg:col-span-4", to: "/teachings" },
  { id: "community", Icon: Rss, title: "Newsfeed & gifting", body: "Share decrees and prayers, say Amen, and gift a friend a month of Premium.", span: "lg:col-span-4", to: "/feed" },
];

export function FeatureShowcase() {
  return (
    <section className="bg-[#F3F0EA]/60 border-y border-[#EAE6DF]" data-testid="feature-showcase-section">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="max-w-2xl">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">Everything in one place</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight mt-3">Built for believers who decree daily.</h2>
        </div>
        <div className="grid lg:grid-cols-12 gap-4 mt-12">
          {FEATURES.map((f, i) => (
            <Link to={f.to} key={f.id} data-testid={`feature-${f.id}`}
              className={`group border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-7 sm:p-8 hover:border-[#9B7E4E] transition-colors duration-300 animate-fade-up ${f.span}`}
              style={{ animationDelay: `${i * 0.06}s` }}>
              <f.Icon className="w-6 h-6 text-[#9B7E4E]" strokeWidth={1.5} />
              <h3 className="font-heading text-xl sm:text-2xl text-[#2C2A29] mt-5 group-hover:underline underline-offset-4 decoration-[#9B7E4E]">{f.title}</h3>
              <p className="font-body text-sm sm:text-base text-[#4A4744] mt-2 leading-relaxed">{f.body}</p>
              {f.id === "generator" && (
                <div className="mt-8 border-l-2 border-[#9B7E4E] pl-5">
                  <p className="font-decree text-lg sm:text-xl text-[#2C2A29] leading-relaxed">I decree that no weapon formed against me prospers, for the Lord is my defender and every accusing tongue is condemned — as Isaiah 54:17 declares over my life.</p>
                  <p className="font-body text-xs uppercase tracking-wide text-[#9B7E4E] mt-2 flex items-center gap-2"><Volume2 className="w-3.5 h-3.5" /> Read aloud with one tap</p>
                </div>
              )}
              {f.id === "community" && <Gift className="w-5 h-5 text-[#B7AE9F] mt-4" strokeWidth={1.5} />}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
